import { fileRecoveryTarget } from "../libraries/fileAccess";
import { RETURN_AFTER_LOGIN_STORAGE_KEY } from "../features/CorrelationDiagram/constants";
import FileAccessPage from "../features/users/FileAccess";
import Loading from "@lhd/components/Loading";
import { ROUTES } from "@lhd/constants";
import AdminCompanyConfig from "@lhd/features/AdminCompanyConfig";
import AppForm, { AppFormCreate } from "@lhd/features/Apps/AppForm";
import Attendance from "@lhd/features/Attendance";
import Branches from "@lhd/features/Branches";
import Company from "@lhd/features/Company";
import MaintenanceNotificationList from "@lhd/features/MaintenanceNotification";
import LegacyEventFormRouteRedirect from "@lhd/features/EventForms/LegacyEventFormRouteRedirect";
import { hasEventFormManagementPermission } from "@lhd/features/EventForms/permissions";
import { loggedInUserSelector, user } from "@lhd/features/users/selector";
import {
  getIsCompanyPoliciesPurchaseBlocked,
  useCanEditWorker,
  useGetAuth,
  useGetPermissions,
  useGetPermissionsEachPage,
  useReAuthenticationOneTime,
  useBrowserPathname,
} from "@lhd/hooks";
import { PERMISSION_KEYS } from "../hooks/useGetPermissions";
import { Company as TypeCompany } from "@lhd/types";
import { getAccessToken, getCompanyId } from "@lhd/utility";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import { find } from "lodash";
import { FC, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import GlobalChat from "../components/GlobalChat";
import CommonLayout from "../components/CommonLayout";
import { setIsMobileMenuOpen } from "../components/CommonLayout/sidebarSlice";
import ProtectedRoute from "../routes/ProtectRoute";
import {
  AppCreateProtected,
  AppDetailProtected,
  AppProtectedRoute,
} from "./AppProtectedRoute";
import AppFormContextRouteLayout from "./AppFormContextRouteLayout";
import CompanyHistoryProtectedRoute from "./CompanyHistoryProtectedRoute";
import withLazy from "./withLazy";

import "@silevis/reactgrid/styles.css";
import "antd/dist/antd.css";
import "antd/dist/antd.min.css";
import "moment/locale/ja";
import "react-datepicker/dist/react-datepicker.css";
import "react-quill/dist/quill.snow.css";
import "react-sortable-tree/style.css";
import CustomerHistoryProtectedRoute from "./CustomerHistoryProtectedRoute";
import ModalForm from "@lhd/features/MaintenanceNotification/modalForm";
import CompanyPolicyCreate from "@lhd/features/CompanyPolicies/CompanyPolicyCreate";
import CompanyPolicyEdit from "@lhd/features/CompanyPolicies/CompanyPolicyEdit";
import CompanyPoliciesList from "@lhd/features/CompanyPolicies/CompanyPoliciesList";
import FinancialReportsList from "@lhd/features/FinancialReports/FinancialReportsList";
import FinancialReportCreate from "@lhd/features/FinancialReports/FinancialReportCreate";
import FinancialReportEdit from "@lhd/features/FinancialReports/FinancialReportEdit";
import BusinessTripReportsList from "@lhd/features/BusinessTripReports/BusinessTripReportsList";
import BusinessTripReportCreate from "@lhd/features/BusinessTripReports/BusinessTripReportCreate";
import BusinessTripReportEdit from "@lhd/features/BusinessTripReports/BusinessTripReportEdit";
import EmploymentContractEdit from "@lhd/features/EmploymentContracts/EmploymentContractEdit";
import EmploymentContractCreate from "@lhd/features/EmploymentContracts/EmploymentContractCreate";
import PreserveQueryParams from "./PreserveQueryParams";
import TrackingTime from "@lhd/features/TrackingTime/HomeTracking";
import ListTracking from "@lhd/features/TrackingTime/ListTracking";
import DetailTracking from "@lhd/features/TrackingTime/DetailTracking";
import { isMobile, isTablet } from "react-device-detect";
import ElectronicLearningChartV2 from "@lhd/features/ElectronicLearningChart/ElectronicLearningChartV2";
import moment from "moment";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";

moment.locale("ja");
dayjs.extend(customParseFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

const Apps = withLazy(() => import("@lhd/features/Apps"));
const AppCreate = withLazy(() => import("@lhd/features/Apps/AppCreate"));
const AppEdit = withLazy(() => import("@lhd/features/Apps/AppEdit"));
const CustomerSheet = withLazy(
  () => import("@lhd/features/Customers/CustomerSheet"),
);
const Divisions = withLazy(() => import("@lhd/features/Divisions"));
const AppCategoryControl = withLazy(
  () => import("@lhd/features/AppCategories/AppCategoriesControl"),
);
const AppCategorysList = withLazy(
  () => import("@lhd/features/AppCategories/AppCategoriesList"),
);
const AppDetail = withLazy(() => import("@lhd/features/Apps/AppDetail"));
const ManagementAppSheet = withLazy(
  () => import("@lhd/features/Apps/AppList/ManagementAppSheet"),
);
const AppPermission = withLazy(
  () => import("@lhd/features/Apps/AppPermission"),
);
const RecordPermission = withLazy(
  () => import("@lhd/features/Apps/AppSetting/RecordPermission"),
);
const AppSubmittedManagement = withLazy(
  () => import("@lhd/features/AppSubmittedManagement"),
);
const CustomerCreation = withLazy(
  () => import("@lhd/features/Customers/NewCustomerControl"),
);
const CustomerListV2 = withLazy(
  () => import("@lhd/features/Customers/CustomerListV2"),
);
const OrganizationChart = withLazy(
  () => import("@lhd/features/OrganizationChart"),
);
const OrganizationChartControl = withLazy(
  () => import("@lhd/features/OrganizationChart/OrganizationChartControl"),
);
const Department = withLazy(
  () => import("@lhd/features/Organizations/Department"),
);
const Management = withLazy(
  () => import("@lhd/features/Organizations/Management"),
);
const Organization = withLazy(
  () => import("@lhd/features/Organizations/Organization"),
);
const Orgs = withLazy(() => import("@lhd/features/Organizations/Orgs"));
const OrgChart = withLazy(() => import("@lhd/features/OrgChart"));
const PositionControl = withLazy(
  () => import("@lhd/features/Position/PositionControl"),
);
const PositionsList = withLazy(
  () => import("@lhd/features/Position/PositionList"),
);
const ManagementTaskSheet = withLazy(
  () => import("@lhd/features/ProjectManagement/ManagementTaskSheet"),
);
const ManagementProjectListSheets = withLazy(
  () =>
    import(
      "@lhd/features/ProjectManagement/ProjectInformation/ManagementProjectListSheet"
    ),
);
const ProjectControl = withLazy(
  () =>
    import("@lhd/features/ProjectManagement/ProjectInformation/ProjectControl"),
);
const ProjectList = withLazy(
  () =>
    import("@lhd/features/ProjectManagement/ProjectInformation/ProjectList"),
);
const ProjectDetail = withLazy(
  () =>
    import(
      "@lhd/features/ProjectManagement/ProjectInformation/ProjectList/ProjectDetail"
    ),
);
const TaskControl = withLazy(
  () =>
    import(
      "@lhd/features/ProjectManagement/ProjectInformation/ProjectList/ProjectDetail/Tasks/TaskControl"
    ),
);
const SalaryFlowChart = withLazy(() => import("@lhd/features/Salary"));
const OrganizationFlowChart = withLazy(
  () => import("@lhd/features/OrganizationSummaries"),
);
const PermissionFlowChart = withLazy(
  () => import("@lhd/features/PermissionSummaries"),
);
const CustomerHistorySummaries = withLazy(
  () => import("@lhd/features/CustomerHistorySummaries"),
);
const AgentSummaries = withLazy(() => import("@lhd/features/AgentSummaries"));
const BizSheetSummaries = withLazy(
  () => import("@lhd/features/Apps/BizSheetChart"),
);
const SalaryConfigMaster = withLazy(() => import("@lhd/features/SalaryConfig"));
const Arrangements = withLazy(
  () => import("@lhd/features/Salary/Arrangements"),
);
const Deductions = withLazy(() => import("@lhd/features/Salary/Deductions"));
const Expenses = withLazy(() => import("@lhd/features/Salary/Expenses"));
const PaymentSheets = withLazy(
  () => import("@lhd/features/Salary/PaymentSheets"),
);
const Provisions = withLazy(() => import("@lhd/features/Salary/Provisions"));
const SalarySheets = withLazy(
  () => import("@lhd/features/Salary/SalarySheets"),
);
const SalarySheetForWorkers = withLazy(
  () => import("@lhd/features/Salary/SalarySheetForWorkers"),
);
const SalaryLedgers = withLazy(
  () => import("@lhd/features/Salary/SalaryLedgers"),
);
const StaffTaxSheets = withLazy(
  () => import("@lhd/features/Salary/StaffTaxSheets"),
);
const Transfers = withLazy(() => import("@lhd/features/Salary/Transfers"));
const Works = withLazy(() => import("@lhd/features/Salary/Works"));
const CompanyCreate = withLazy(
  () => import("@lhd/features/UserCompanies/NewCompanySheet"),
);
const CompanyList = withLazy(
  () => import("@lhd/features/UserCompanies/CompanyList"),
);
const Login = withLazy(() => import("@lhd/features/users/Login"));
const CorrelationDiagramV2 = withLazy(
  () => import("@lhd/features/CorrelationDiagram/CorrelationDiagramV2"),
);

// const SalaryConfig from "@lhd/features/SalaryConfig";
const AppraisalControl = withLazy(
  () => import("@lhd/features/Appraisal/AppraisalControl"),
);
const AppraisalList = withLazy(
  () => import("@lhd/features/Appraisal/AppraisalList"),
);
const AppraisalAnalyst = withLazy(
  () => import("@lhd/features/AppraisalAnalyst"),
);
const AppraisalKgiList = withLazy(
  () => import("@lhd/features/AppraisalKgiList"),
);
const AppraisalSetting = withLazy(
  () => import("@lhd/features/AppraisalSetting"),
);
const AppraisalSheetControl = withLazy(
  () => import("@lhd/features/AppraisalSheet/AppraisalSheetControl"),
);
const AppraisalSheetList = withLazy(
  () => import("@lhd/features/AppraisalSheet/AppraisalSheetList"),
);
const AppraisalSummaries = withLazy(
  () => import("@lhd/features/Appraisal/AppraisalSummaries"),
);
const ElectronicLearning = withLazy(
  () => import("@lhd/features/ElectronicLearning"),
);
const CourseControlCreate = withLazy(
  () =>
    import(
      "@lhd/features/ElectronicLearning/components/LearningList/components/CourseControl/pages/CreatePage"
    ),
);
const CourseControlEdit = withLazy(
  () =>
    import(
      "@lhd/features/ElectronicLearning/components/LearningList/components/CourseControl/pages/EditPage"
    ),
);
const CourseDetail = withLazy(
  () =>
    import(
      "@lhd/features/ElectronicLearning/components/LearningList/components/CourseDetail"
    ),
);
const ElectronicLearningStore = withLazy(
  () => import("@lhd/features/ElectronicLearningStore"),
);
const CalendarMeetingList = withLazy(
  () => import("@lhd/features/ProjectManager/components/CalendarMeetingList"),
);
const Kpi = withLazy(() => import("@lhd/features/Kpi"));
const NotFoundPage = withLazy(() => import("@lhd/features/NotFoundPage"));
const ReloadPage = withLazy(() => import("@lhd/features/ReloadPage"));
const AnnualList = withLazy(() => import("@lhd/features/Salary/AnnualList"));
const AnnualPermissions = withLazy(
  () => import("@lhd/features/Salary/AnnualPermissions"),
);
const PayslipYear = withLazy(() => import("@lhd/features/Salary/PayslipYear"));
const CompanyDetail = withLazy(
  () => import("@lhd/features/UserCompanies/CompanyDetail"),
);
const ContractCreate = withLazy(
  () =>
    import("@lhd/features/UserCompanies/CompanyDetail/Contract/ContractCreate"),
);
const ContractDetail = withLazy(
  () =>
    import("@lhd/features/UserCompanies/CompanyDetail/Contract/ContractDetail"),
);
const WorkerDetail = withLazy(
  () => import("@lhd/features/Workers/WokerDetail"),
);
const WorkerControl = withLazy(
  () => import("@lhd/features/Workers/WorkerCreate"),
);
const WorkersListV2 = withLazy(
  () => import("@lhd/features/Workers/WorkerListV2"),
);
const WorkerSheetV2 = withLazy(
  () => import("@lhd/features/Workers/WorkerSheetV2"),
);
const ListMess = withLazy(() => import("@lhd/features/ListMess"));
const ProtectedRoutes = withLazy(() => import("../routes/ProtectRoute"));
const ELearningEditProtectedRoute = withLazy(
  () => import("./ELearningEditProtectedRoute"),
);
const SalaryProtectedRoute = withLazy(() => import("./SalaryProtectedRoute"));
const SettingCategories = withLazy(
  () => import("@lhd/features/SettingCategories"),
);
const ContractManagement = withLazy(
  () => import("@lhd/features/ContractManagement"),
);
const CustomerDetail = withLazy(
  () => import("@lhd/features/Customers/CustomerDetail"),
);

const ProjectManagerList = withLazy(
  () => import("@lhd/features/ProjectManager/ProjectMgmtMain"),
);
const ProjectDetailMain = withLazy(
  () => import("@lhd/features/ProjectManager/ProjectDetail"),
);
const ProjectManagerEdit = withLazy(
  () => import("@lhd/features/ProjectManager/ProjectEdit"),
);
const ProjectTaskView = withLazy(
  () => import("@lhd/features/ProjectManager/components/Task/TaskView"),
);
const ProjectTaskEdit = withLazy(
  () => import("@lhd/features/ProjectManager/components/Task/TaskEdit"),
);
const ProjectMeetingCreate = withLazy(
  () => import("@lhd/features/ProjectManager/components/ProjectMeeting/create"),
);
const ProjectMeetingEdit = withLazy(
  () => import("@lhd/features/ProjectManager/components/ProjectMeeting/edit"),
);
const ProjectMeetingDetail = withLazy(
  () => import("@lhd/features/ProjectManager/components/ProjectMeeting/detail"),
);
const ProjectMeetingHistory = withLazy(
  () =>
    import("@lhd/features/ProjectManager/components/ProjectMeeting/history"),
);
const ProjectMeetingHistoryDetail = withLazy(
  () =>
    import(
      "@lhd/features/ProjectManager/components/ProjectMeeting/history/detail"
    ),
);
const ProjectPhaseEditable = withLazy(
  () => import("@lhd/features/ProjectManager/components/ProjectPhase/Edit"),
);
const ProjectFlowChart = withLazy(
  () => import("@lhd/features/ProjectManager/components/ProjectFlowChart"),
);
const WorkerInviteChart = withLazy(
  () => import("@lhd/features/Workers/WorkerInviteChart"),
);
const SettingStatuses = withLazy(() => import("@lhd/features/SettingStatuses"));

const ContractPayment = withLazy(
  () => import("@lhd/features/ContractManagement/ContractPayment"),
);
const PaymentHistory = withLazy(
  () => import("@lhd/features/ContractManagement/PaymentHistory"),
);
const ContractStaffList = withLazy(
  () => import("@lhd/features/ContractManagement/ContractStaffList"),
);
const ElectronicLearningChart = withLazy(
  () => import("@lhd/features/ElectronicLearningChart"),
);
const CompanyPoliciesChart = withLazy(
  () => import("@lhd/features/CompanyPolicies/CompanyPoliciesChart"),
);
const BusinessControlsChart = withLazy(
  () => import("@lhd/features/InternalControl/BusinessControlsChart"),
);
const CompanyPolicyProtectedRoute = withLazy(
  () => import("./CompanyPolicyProtectedRoute"),
);
const RolePermissionCreate = withLazy(
  () => import("@lhd/features/RolePermissions/RolePermissionCreate"),
);

const RolePermissionEdit = withLazy(
  () => import("@lhd/features/RolePermissions/RolePermissionEdit"),
);

const RolePermissionList = withLazy(
  () => import("@lhd/features/RolePermissions/RolePermissionsList"),
);
const RolePermissionSheet = withLazy(
  () => import("@lhd/features/RolePermissions/RolePermissionsSheet"),
);
const LaborDocumentCreate = withLazy(
  () => import("@lhd/features/LaborDocuments/LaborDocumentCreate"),
);
const LaborDocumentEdit = withLazy(
  () => import("@lhd/features/LaborDocuments/LaborDocumentEdit"),
);
const BusinessFlowList = withLazy(
  () => import("@lhd/features/InternalControl/BusinessFlow/BusinessFlowList"),
);
const BusinessFlowEdit = withLazy(
  () => import("@lhd/features/InternalControl/BusinessFlow/BusinessFlowEdit"),
);
const BusinessFlowCreate = withLazy(
  () => import("@lhd/features/InternalControl/BusinessFlow/BusinessFlowCreate"),
);
const BusinessFlowSheet = withLazy(
  () => import("@lhd/features/InternalControl/BusinessFlow/BusinessFlowSheet"),
);
const BusinessManualsList = withLazy(
  () =>
    // import("@lhd/features/InternalControl/BusinessManuals/BusinessManualsList"),
    import("@lhd/features/InternalControl/BusinessManuals/BusinessManualsList"),
);
const BusinessManualsEdit = withLazy(
  () =>
    import("@lhd/features/InternalControl/BusinessManuals/BusinessManualsEdit"),
);
const BusinessManualsCreate = withLazy(
  () =>
    import(
      "@lhd/features/InternalControl/BusinessManuals/BusinessManualsCreate"
    ),
);
const BusinessManualsSheet = withLazy(
  () =>
    import(
      "@lhd/features/InternalControl/BusinessManuals/BusinessManualsSheet"
    ),
);

const MOBILE_TRACKING_ALLOWED_PATHS = [
  `/${ROUTES.SIGNIN}`,
  ROUTES.TRACKING_TIME,
  `${ROUTES.TRACKING_TIME}${ROUTES.LIST}`,
  `${ROUTES.TRACKING_TIME}${ROUTES.DETAIL}`,
];

const isMobileTrackingAllowedPath = (pathname: string) =>
  MOBILE_TRACKING_ALLOWED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
const BusinessRpgList = withLazy(
  () => import("@lhd/features/BusinessRpg/List"),
);
const BusinessRpg = withLazy(() => import("@lhd/features/BusinessRpg"));
const FireRpgFamilyEventSetting = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgFamilyEventSetting"),
);
const FireRpgMicroEventSetting = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgMicroEventSetting"),
);
const FireRpgMacroEventSetting = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgMacroEventSetting"),
);
const FireMarketTable = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgMarketChart"),
);
const FireJobMaster = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgJobConfig"),
);
const LatestTrialBalanceFireCreate = withLazy(
  () => import("@lhd/features/LatestTrialBalanceFire/CreatePage"),
);
const LatestTrialBalanceFireEdit = withLazy(
  () => import("@lhd/features/LatestTrialBalanceFire/EditPage"),
);
const FireRpgGameSetting = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgGameSetting"),
);
const FireRpgMarketEconomyView = withLazy(
  () => import("@lhd/features/BusinessRpg/FireRpgMarketEconomyView"),
);
const AdminEventFormsPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.AdminEventFormsPage,
  })),
);
const AdminEventFormDetailPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.AdminEventFormDetailPage,
  })),
);
const PublicEventFormPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.PublicEventFormPage,
  })),
);
const EventFormStatusPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.EventFormStatusPage,
  })),
);
const EventFormApplicationVerificationPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.EventFormApplicationVerificationPage,
  })),
);
const EventFormPolicyPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.EventFormPolicyPage,
  })),
);
const InitialPasswordSetupPage = withLazy(() =>
  import("@lhd/features/EventForms").then((module) => ({
    default: module.InitialPasswordSetupPage,
  })),
);

const RootLayout: FC = memo(function RootLayout() {
  const isMobileOrTablet = isMobile || isTablet;
  const { isUserResigned } = useGetAuth();
  const hideGlobalChatMobileWorkerTracking = isMobileOrTablet || isUserResigned;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isMobile) return;

    const handlePopState = () => {
      setTimeout(() => {
        dispatch(setIsMobileMenuOpen(true));
      }, 50);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch]);

  return (
    <>
      <PreserveQueryParams />
      <Outlet />
      {!hideGlobalChatMobileWorkerTracking && <GlobalChat />}
    </>
  );
});

const AppRoutes: FC = () => {
  const accessToken = getAccessToken();
  const currentUser = useSelector(loggedInUserSelector) || Boolean(accessToken);
  const { auth, isUserResigned } = useGetAuth();
  const pathname = useBrowserPathname();
  const isPublicCorrelationDiagram =
    pathname === `/${ROUTES.CORRELATION_DIAGRAM}`;
  const isAuthPage =
    pathname === `/${ROUTES.SIGNIN}` ||
    pathname.startsWith(`/${ROUTES.SIGNIN}/`) ||
    pathname === `/${ROUTES.PASSWORD_RESET}`;
  useEffect(() => {
    if (pathname !== "/file-access") return;
    const target = fileRecoveryTarget(window.location.search);
    if (target) sessionStorage.setItem(RETURN_AFTER_LOGIN_STORAGE_KEY, target);
  }, [pathname]);
  const shouldCallFetchMe = Boolean(accessToken) && !isPublicCorrelationDiagram;
  const { isLoading } = useReAuthenticationOneTime(shouldCallFetchMe);
  const skipGlobalLoading =
    Boolean(accessToken) && (isAuthPage || isPublicCorrelationDiagram);
  const permission = useGetPermissions();
  const permissionPage = useGetPermissionsEachPage();
  const canEditWorker = useCanEditWorker();
  const { isSuperAdmin } = useGetAuth();
  const isAdmin = !!auth?.is_admin;
  const canAccessContractTab =
    (isAdmin ||
      isSuperAdmin ||
      permissionPage[ROUTES.AGREEMENTS]?.all ||
      permissionPage[ROUTES.AGREEMENTS]?.create) &&
    !isUserResigned;
  const canManageEventForms = hasEventFormManagementPermission(auth);
  const arrangmentDetailPermission = isUserResigned
    ? true
    : permissionPage[ROUTES.AGREEMENTS]?.view;

  const laborDetailPermission = isUserResigned
    ? true
    : permissionPage[ROUTES.LABOR_DOCUMENTS]?.all ||
      permissionPage[ROUTES.LABOR_DOCUMENTS]?.view ||
      permissionPage[ROUTES.LABOR_DOCUMENTS]?.edit;

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId && auth) {
      const companyActive =
        (find(
          auth?.companies,
          (company) => company.is_work_default,
        ) as TypeCompany) || auth?.companies[0];
      localStorage.setItem("company_id", companyActive?.id?.toString());
    }
  }, [auth, auth?.companies]);

  const router = createBrowserRouter([
    {
      element: <RootLayout />,
      children: [
        { path: "file-access", element: <FileAccessPage /> },
        {
          path: "*",
          element: <NotFoundPage />,
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.EVENT_FORM_PUBLIC,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <PublicEventFormPage />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.EVENT_FORM_STATUS,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <EventFormStatusPage />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.EVENT_FORM_VERIFICATION,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <EventFormApplicationVerificationPage />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.EVENT_FORM_POLICY,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <EventFormPolicyPage />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.PASSWORD_RESET,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <InitialPasswordSetupPage />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.LEGACY_EVENT_FORM_PUBLIC,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <LegacyEventFormRouteRedirect route="publicForm" />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.LEGACY_EVENT_FORM_STATUS,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <LegacyEventFormRouteRedirect route="status" />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.LEGACY_EVENT_FORM_POLICY,
          element: (
            <ProtectedRoutes
              condition={!isUserResigned}
              redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
            >
              <LegacyEventFormRouteRedirect route="policy" />
            </ProtectedRoutes>
          ),
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              isLoading={false}
              specialRoute={true}
            >
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ROUTES.HOME,
              element: <Company />,
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ROUTES.BUSINESS_RPG,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessRpgList />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.BUSINESS_RPG_PLAY,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessRpg />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_FAMILY_EVENT_SETTING,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireRpgFamilyEventSetting />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_GAME_SETTING,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireRpgGameSetting />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_MICRO_EVENT_SETTING,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireRpgMicroEventSetting />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_MACRO_EVENT_SETTING,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireRpgMacroEventSetting />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_MARKET_CHART,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireMarketTable />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_MARKET_TABLE,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireMarketTable />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_JOB_CONFIGS,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireJobMaster />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FIRE_RPG_MARKET_ECONOMY_VIEW,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FireRpgMarketEconomyView />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.LATEST_TRIAL_BALANCE_FIRE,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <LatestTrialBalanceFireCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.LATEST_TRIAL_BALANCE_FIRE}${ROUTES.DETAIL}`,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <LatestTrialBalanceFireEdit />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.EVENT_FORMS,
              element: (
                <ProtectedRoutes
                  condition={
                    canManageEventForms &&
                    isAdmin &&
                    permission.isContractStatusAccepted &&
                    !isUserResigned
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <AdminEventFormsPage />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.EVENT_FORM_DETAIL,
              element: (
                <ProtectedRoutes
                  condition={
                    canManageEventForms &&
                    permission.isContractStatusAccepted &&
                    !isUserResigned
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <AdminEventFormDetailPage />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.LEGACY_EVENT_FORMS,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <LegacyEventFormRouteRedirect route="admin" />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.LEGACY_EVENT_FORM_DETAIL,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <LegacyEventFormRouteRedirect route="adminDetail" />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.COMPANY_POLICIES,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.COMPANY_POLICIES]?.all ||
                    permissionPage[ROUTES.COMPANY_POLICIES]?.view ||
                    permissionPage[ROUTES.COMPANY_POLICIES]?.edit
                  }
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <CompanyPoliciesList />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.BUSINESS_MANUALS,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.all ||
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.view ||
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessManualsList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <CompanyPolicyProtectedRoute
                  appType={APP_TYPES.BUSINESS_MANUALS}
                >
                  <BusinessManualsEdit />
                </CompanyPolicyProtectedRoute>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_MANUALS]?.create}
                  isLoading={permissionPage[ROUTES.BUSINESS_MANUALS]?.isLoading}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessManualsCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.SHEET}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_MANUALS]?.view}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <BusinessManualsSheet />
                </ProtectedRoutes>
              ),
            },
            {
              path: "app/86",
              element: <Navigate to={`/${ROUTES.BUSINESS_MANUALS}`} replace />,
            },
            {
              path: `${ROUTES.COMPANY_POLICIES}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <CompanyPolicyProtectedRoute
                  appType={APP_TYPES.COMPANY_POLICIES}
                >
                  <CompanyPolicyEdit />
                </CompanyPolicyProtectedRoute>
              ),
            },
            {
              path: `${ROUTES.COMPANY_POLICIES}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.COMPANY_POLICIES]?.all ||
                    permissionPage[ROUTES.COMPANY_POLICIES]?.create
                  }
                  isLoading={
                    permission.isLoading ||
                    !!permissionPage[ROUTES.COMPANY_POLICIES]?.isLoading
                  }
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <CompanyPolicyCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.FINANCIAL_REPORTS,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.all ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.view ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.edit ||
                    true
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FinancialReportsList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.FINANCIAL_REPORTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.all ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.edit ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.view ||
                    true
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FinancialReportEdit />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.FINANCIAL_REPORTS}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.all ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.create ||
                    permissionPage[ROUTES.FINANCIAL_REPORTS]?.delete
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <FinancialReportCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.BUSINESS_TRIP_REPORTS,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.all ||
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.view ||
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessTripReportsList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_TRIP_REPORTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.all ||
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.view ||
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessTripReportEdit />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_TRIP_REPORTS}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.all ||
                    permissionPage[ROUTES.BUSINESS_TRIP_REPORTS]?.create
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessTripReportCreate />
                </ProtectedRoutes>
              ),
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: `${ROUTES.EMPLOYMENT_CONTRACTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}/:companyId`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]?.all ||
                    permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]?.view ||
                    permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <EmploymentContractEdit
                    configShowBtnsOnHeader={{
                      btnSave: true,
                      saveAsNew: true,
                      btnShare: true,
                      btnDuplicate: true,
                      btnToCreate: true,
                      btnPrintPdf: true,
                    }}
                    permission={permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]}
                  />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.EMPLOYMENT_CONTRACTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}/:companyId/:role`,
              element: (
                <EmploymentContractEdit
                  worker={true}
                  permission={{
                    view: true,
                    edit: true,
                    delete: false,
                    all: false,
                    create: false,
                  }}
                  configShowBtnsOnHeader={{
                    btnPrintPdf: true,
                  }}
                />
              ),
            },
            {
              path: `${ROUTES.EMPLOYMENT_CONTRACTS}/${ROUTES.APP_ID}/${ROUTES.CREATE}/:companyId`,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]?.all ||
                    permissionPage[ROUTES.EMPLOYMENT_CONTRACTS]?.create
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <EmploymentContractCreate />
                </ProtectedRoutes>
              ),
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ROUTES.ROLE_PERMISSIONS,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.ROLE_PERMISSIONS]?.all ||
                    permissionPage[ROUTES.ROLE_PERMISSIONS]?.view ||
                    permissionPage[ROUTES.ROLE_PERMISSIONS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <RolePermissionList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.ROLE_PERMISSIONS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <CompanyPolicyProtectedRoute
                  appType={APP_TYPES.ROLE_PERMISSIONS}
                >
                  <RolePermissionEdit />
                </CompanyPolicyProtectedRoute>
              ),
            },
            {
              path: `${ROUTES.ROLE_PERMISSIONS}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.ROLE_PERMISSIONS]?.create}
                  isLoading={permissionPage[ROUTES.ROLE_PERMISSIONS]?.isLoading}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <RolePermissionCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.ROLE_PERMISSIONS}/${ROUTES.APP_ID}/${ROUTES.SHEET}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.ROLE_PERMISSIONS]?.view}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <RolePermissionSheet />
                </ProtectedRoutes>
              ),
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ROUTES.BUSINESS_MANUALS,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.all ||
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.view ||
                    permissionPage[ROUTES.BUSINESS_MANUALS]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessManualsList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <CompanyPolicyProtectedRoute
                  appType={APP_TYPES.BUSINESS_MANUALS}
                >
                  <BusinessManualsEdit />
                </CompanyPolicyProtectedRoute>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_MANUALS]?.create}
                  isLoading={permissionPage[ROUTES.BUSINESS_MANUALS]?.isLoading}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessManualsCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_MANUALS}/${ROUTES.APP_ID}/${ROUTES.SHEET}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_MANUALS]?.view}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessManualsSheet />
                </ProtectedRoutes>
              ),
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          //TODO:
          children: [
            {
              path: ROUTES.ANNUAL_SALARY_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={
                        isUserResigned
                          ? permission[ROUTES.ANNUAL_SALARY_SUMMARIES]
                          : permission.salarySummariesAccess
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                      specialRoute={true}
                    >
                      <SalaryFlowChart />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ANNUAL_SALARY_LIST,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.ANNUAL_SALARY_LIST]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                      specialRoute={true}
                    >
                      <AnnualList activeKey="salary" />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ANNUAL_PAYMENT_LIST,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.ANNUAL_SALARY_LIST]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                      specialRoute={true}
                    >
                      <AnnualList activeKey="payment" />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.SALARY_LEDGERS,
                  element: (
                    <SalaryProtectedRoute type={ROUTES.SALARY_LEDGERS}>
                      <SalaryLedgers />
                    </SalaryProtectedRoute>
                  ),
                },
                {
                  path: ROUTES.YEAR_SLUG,
                  children: [
                    {
                      path: ROUTES.SALARY_SHEETS,
                      element: (
                        <SalaryProtectedRoute type={ROUTES.SALARY_SHEETS}>
                          <SalarySheets />
                        </SalaryProtectedRoute>
                      ),
                    },
                    {
                      path: ROUTES.SALARY_SHEET_FOR_WORKERS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.ANNUAL_PAYMENT_LIST]}
                          redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                        >
                          <SalaryProtectedRoute
                            type={ROUTES.SALARY_SHEET_FOR_WORKERS}
                          >
                            <SalarySheetForWorkers />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.PAYMENT_SHEETS,
                      element: (
                        <SalaryProtectedRoute type={ROUTES.PAYMENT_SHEETS}>
                          <PaymentSheets />
                        </SalaryProtectedRoute>
                      ),
                    },
                    {
                      path: ROUTES.STAFF_TAX_SHEETS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <StaffTaxSheets />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.TRANSFERS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <Transfers />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.DEDUCTIONS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <Deductions />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.PROVISIONS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <PayslipYear>
                              <Provisions />
                            </PayslipYear>
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.EXPENSES,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <Expenses />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.WORKS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[PERMISSION_KEYS.FULL_SALARY]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute>
                            <Works />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.ARRANGEMENTS,
                      element: (
                        <ProtectedRoutes
                          condition={!isUserResigned}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <SalaryProtectedRoute type={ROUTES.ARRANGEMENTS}>
                            <Arrangements />
                          </SalaryProtectedRoute>
                        </ProtectedRoutes>
                      ),
                    },
                  ],
                },
              ],
            },

            {
              path: ROUTES.ANNUAL_ORG_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.ANNUAL_ORG_SUMMARIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                      specialRoute={true}
                    >
                      <OrganizationFlowChart />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.WORKER_INVITE_CHART,
                  element: (
                    <ProtectedRoutes
                      condition={canEditWorker}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <WorkerInviteChart />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.YEAR_SLUG,
                  element: (
                    <ProtectedRoutes
                      condition={!isUserResigned}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <Outlet />
                    </ProtectedRoutes>
                  ),
                  children: [
                    {
                      path: ROUTES.ORGANIZATIONS,
                      element: <Organization />,
                    },
                    {
                      path: ROUTES.DEPARTMENTS,
                      element: <Department />,
                    },
                    {
                      path: ROUTES.DIVISIONS,
                      element: <Divisions />,
                    },
                    {
                      path: ROUTES.BRANCH,
                      element: <Branches />,
                    },
                    {
                      path: ROUTES.POSITIONS,
                      element: <PositionsList />,
                    },
                  ],
                },
              ],
            },

            {
              path: ROUTES.PERMISSION_SUMMARIES,
              element: (
                <ProtectedRoutes
                  condition={
                    !isUserResigned && permission[ROUTES.PERMISSION_SUMMARIES]
                  }
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <PermissionFlowChart />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.ELECTRONIC_LEARNING_CHART,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={
                        !isUserResigned && !!permission.eLearningChartAccess
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ElectronicLearningChartV2 />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.COMPANY_POLICIES_CHART,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={
                        !getIsCompanyPoliciesPurchaseBlocked(
                          auth?.current_contract_status,
                          permission.companyPolicyEnabled,
                        ) && !isUserResigned
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CompanyPoliciesChart />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.BUSINESS_CONTROLS_CHART,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={!isUserResigned}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <BusinessControlsChart />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.AGENT_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.AGENT_SUMMARIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AgentSummaries />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.CUSTOMER_HISTORY_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={
                        !isUserResigned && permission[ROUTES.CUSTOMER_LIST]
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CustomerHistorySummaries />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.BIZ_SHEET_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={
                        !!permission[ROUTES.APPS] &&
                        !!permission.defaultBasicPackage &&
                        !isUserResigned
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <BizSheetSummaries />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.SALARY_CONFIGURATION,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <SalaryProtectedRoute>
                    <SalaryConfigMaster />
                  </SalaryProtectedRoute>
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.CALENDAR,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <CalendarMeetingList />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.KPI,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.KPI]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <Kpi />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.KPI_PLANS,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.KPI]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <Kpi />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.KPI_DETAIL,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.KPI]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <Kpi />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.Electronic_Learning,
              children: [
                {
                  path: "",
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.Electronic_Learning]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ElectronicLearning />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.STORE}`,
                  element: (
                    <ProtectedRoutes
                      condition={permission.eLearningStore}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ElectronicLearningStore />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.COURSES}/${ROUTES.CREATE}`,
                  element: (
                    <ProtectedRoutes
                      condition={permission.eLearningCourseCreate}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CourseControlCreate />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.COURSES}/${ROUTES.ID}/${ROUTES.EDIT}`,
                  element: (
                    <ELearningEditProtectedRoute
                      condition={
                        permission.eLearningCourseEdit ||
                        permission[ROUTES.Electronic_Learning]
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CourseControlEdit />
                    </ELearningEditProtectedRoute>
                  ),
                },
                {
                  path: ROUTES.COURSE_DETAIL,
                  children: [
                    {
                      path: ROUTES.ID,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.Electronic_Learning]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <CourseDetail />
                        </ProtectedRoutes>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.WORKERS,
              children: [
                {
                  index: true,
                  element: <WorkersListV2 />,
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.WORKERS_CREATE]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <WorkerControl />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: <WorkerDetail />,
                },
                {
                  path: ROUTES.WORKER_SHEETS,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.WORKER_SHEETS]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <WorkerSheetV2 />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.COMPANY_CONFIGURATION,
              children: [
                {
                  path: ROUTES.ID,
                  element: (
                    <CompanyHistoryProtectedRoute isConfiguration>
                      <CompanyDetail isConfiguration />
                    </CompanyHistoryProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: ROUTES.HOME,
              children: [
                {
                  path: `${ROUTES.AGREEMENTS}/${ROUTES.APP_ID}/${ROUTES.CREATE}/${ROUTES.COMPANY_ID}`,
                  element: (
                    <ProtectedRoutes
                      condition={canAccessContractTab}
                      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    >
                      <ContractCreate />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.AGREEMENTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}/${ROUTES.COMPANY_ID}`,
                  element: (
                    <ProtectedRoutes
                      condition={
                        permissionPage[ROUTES.AGREEMENTS]?.all ||
                        permissionPage[ROUTES.AGREEMENTS]?.view ||
                        permissionPage[ROUTES.AGREEMENTS]?.edit
                      }
                      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    >
                      <ContractDetail
                        configShowBtnsOnHeader={{
                          btnSave: true,
                          saveAsNew: true,
                          btnShare: true,
                          btnDuplicate: true,
                          btnToCreate: true,
                          btnPrintPdf: true,
                        }}
                      />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.AGREEMENTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}/${ROUTES.COMPANY_ID}/:role`,
                  element: (
                    // <ProtectedRoutes
                    //   condition={arrangmentDetailPermission}
                    //   redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    //   specialRoute={true}
                    // >
                    <ContractDetail
                      worker={true}
                      configShowBtnsOnHeader={{
                        btnPrintPdf: true,
                      }}
                    />
                    // </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.COMPANIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.COMPANIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CompanyList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: "company-detail",
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.COMPANIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CompanyDetail />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission.companyCreate}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CompanyCreate />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <CompanyHistoryProtectedRoute>
                      <CompanyDetail />
                    </CompanyHistoryProtectedRoute>
                  ),
                },
              ],
            },
            {
              path: ROUTES.ADMIN_CONFIG,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.ADMIN_CONFIG]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AdminCompanyConfig />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_SUMMARIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisal}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalSummaries />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_LIST,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalControl />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalControl />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_SETTING,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalSetting />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_ANALYST,
              children: [
                {
                  path: "",
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalAnalyst />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_KGI_LIST,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisalAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalKgiList />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APPRAISAL_SHEET_LIST,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisal}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalSheetList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisal}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalSheetControl />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appraisal}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppraisalSheetControl />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.POSITIONS,
              children: [
                {
                  path: "",
                  element: (
                    <ProtectedRoutes
                      condition={permission.canChangeUser}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <PositionsList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission.canChangeUser}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <PositionControl />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission.canChangeUser}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <PositionControl />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APP_CATEGORIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APP_CATEGORIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppCategorysList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APP_CATEGORIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppCategoryControl pageMode="create" />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APP_CATEGORIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppCategoryControl />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.APP_SUBMITTED_MANAGEMENT,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APP_SUBMITTED_MANAGEMENT]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppSubmittedManagement />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.MAINTENANCE_NOTIFICATION,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.MAINTENANCE_NOTIFICATION]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <MaintenanceNotificationList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.MAINTENANCE_NOTIFICATION]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ModalForm pageMode="create" />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.MAINTENANCE_NOTIFICATION]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ModalForm />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.BRANCH,
              element: <Branches />,
            },
            {
              path: ROUTES.APPS,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APPS]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <Apps />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission.appCreate}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppFormCreate />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: "permission",
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.APPS]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <AppPermission />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: <AppProtectedRoute />,
                  children: [
                    {
                      element: <AppFormContextRouteLayout />,
                      children: [
                        {
                          index: true,
                          element: <AppDetail />,
                        },
                        {
                          path: "edit",
                          element: <AppForm />,
                        },
                        {
                          path: "add",
                          element: <AppForm />,
                        },

                        {
                          path: "app-sheets/:idRecord/:idTable",
                          element: <ManagementAppSheet />,
                        },
                        {
                          path: "app-sheets",
                          element: <ManagementAppSheet />,
                        },
                        {
                          path: "records/edit/:id",
                          element: <AppEdit />,
                        },
                        {
                          path: "records",
                          element: <AppDetail />,
                        },
                        {
                          path: "records/create",
                          element: (
                            <AppCreateProtected>
                              <AppCreate />
                            </AppCreateProtected>
                          ),
                        },
                        {
                          path: "app-grid",
                          element: <AppDetail />,
                        },
                        {
                          path: "app-table",
                          element: <AppDetail />,
                        },
                        {
                          path: "app-information",
                          element: <AppDetail />,
                        },
                        {
                          path: "records/app-sheets",
                          element: (
                            <AppDetailProtected>
                              <AppDetail />
                            </AppDetailProtected>
                          ),
                        },
                        {
                          path: "permission",
                          element: <AppDetail />,
                        },
                        {
                          path: "record-permission",
                          element: <RecordPermission />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.ATTENDANCE,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <Attendance />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.TRACKING_TIME.replace(/^\//, "")}`,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={!isUserResigned}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <TrackingTime />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.LIST.replace(/^\//, ""),
                  element: (
                    <ProtectedRoutes
                      condition={!isUserResigned}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ListTracking />
                    </ProtectedRoutes>
                  ),
                  errorElement: <ReloadPage />,
                },
                {
                  path: ROUTES.DETAIL.replace(/^\//, ""),
                  element: (
                    <ProtectedRoutes
                      condition={!isUserResigned}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <DetailTracking />
                    </ProtectedRoutes>
                  ),
                  errorElement: <ReloadPage />,
                },
              ],
            },
            {
              path: ROUTES.CUSTOMER_LIST,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.CUSTOMER_LIST]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CustomerListV2 />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.CUSTOMER_LIST]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CustomerCreation />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <CustomerHistoryProtectedRoute>
                      <CustomerDetail />
                    </CustomerHistoryProtectedRoute>
                  ),
                },
                {
                  path: ROUTES.CUSTOMER_SHEET,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.CUSTOMER_LIST]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CustomerSheet />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.PROJECT_MANAGE,
              children: [
                {
                  path: "",
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.PROJECT_MANAGE]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      {/* <ProjectManagement /> */}
                      <NotFoundPage />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CUSTOMER_LIST,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.PROJECT_MANAGE]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <CustomerListV2 />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.PROJECT_LIST,
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ProjectList />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.CREATE,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ProjectControl />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.ID,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ProjectControl />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.PROJECT_LIST_SHEETS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ManagementProjectListSheets />
                        </ProtectedRoutes>
                      ),
                    },
                    // {
                    //   path: ROUTES.ID,
                    //   children: [
                    //     {
                    //       path: "",
                    //       element: <ProjectDetail />,
                    //     },
                    //     { path: ROUTES.ID, element: <TaskControl /> },
                    //   ],
                    // },
                  ],
                },
                {
                  path: `${ROUTES.ID}/${ROUTES.TASKS}`,
                  children: [
                    {
                      path: "",
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ProjectDetail />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.CREATE,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <TaskControl />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.ID,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <TaskControl />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.TASKS_SHEETS,
                      element: (
                        <ProtectedRoutes
                          condition={permission[ROUTES.PROJECT_MANAGE]}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ManagementTaskSheet />
                        </ProtectedRoutes>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.PROJECT_MANAGER_LIST,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <Outlet />
                </ProtectedRoutes>
              ),
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.PROJECT_MANAGE]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <ProjectManagerList />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  children: [
                    {
                      index: true,
                      element: (
                        <ProtectedRoutes
                          condition={!isUserResigned}
                          redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                        >
                          <ProjectDetailMain />
                        </ProtectedRoutes>
                      ),
                    },
                    {
                      path: ROUTES.EDIT,
                      element: <ProjectManagerEdit />,
                    },
                    {
                      path: ROUTES.PROJECT_PHASE,
                      children: [
                        {
                          path: ROUTES.PROJECT_PHASE_ID,
                          element: <ProjectPhaseEditable />,
                          children: [
                            {
                              path: ROUTES.EDIT,
                              element: <ProjectPhaseEditable />,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: ROUTES.PROJECT_CHART,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.PROJECT_MANAGE]}
                      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    >
                      <ProjectFlowChart />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.PROJECT_MANAGER_TASK_VIEW,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectTaskView />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_TASK_EDIT,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectTaskEdit />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_MEETING_CREATE,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectMeetingCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_MEETING_EDIT,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectMeetingEdit />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_MEETING_DETAIL,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectMeetingDetail />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_MEETING_HISTORY,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectMeetingHistory />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PROJECT_MANAGER_MEETING_HISTORY_DETAIL,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PROJECT_MANAGE]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ProjectMeetingHistoryDetail />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.ORGANIZATIONS,
              element: <Organization />,
            },
            {
              path: ROUTES.MANAGEMENT,
              element: <Management />,
            },
            {
              path: ROUTES.DEPARTMENTS,
              element: <Department />,
            },
            {
              path: ROUTES.ORGS,
              children: [
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.ORGS]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <Orgs />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.DIVISIONS,
              element: <Divisions />,
            },
            {
              path: ROUTES.ORGANIZATION_CHART,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permissionPage[ROUTES.ORGANIZATION_CHART]?.all}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <OrganizationChart />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.CREATE,
                  element: (
                    <ProtectedRoutes
                      condition={
                        permissionPage[ROUTES.ORGANIZATION_CHART]?.create
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <OrganizationChartControl />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: ROUTES.ID,
                  element: (
                    <ProtectedRoutes
                      condition={
                        permissionPage[ROUTES.ORGANIZATION_CHART]?.edit
                      }
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <OrganizationChartControl />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.BRANCH_CHART,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.BRANCH_CHART]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <OrgChart />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.AUTH,
              children: [
                {
                  path: ROUTES.WORKERS,
                  children: [
                    {
                      path: ROUTES.ID,
                      element: <WorkerControl />,
                    },
                  ],
                },
              ],
            },

            {
              path: ROUTES.SETTING_CATEGORIES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={permission[ROUTES.SETTING_CATEGORIES]}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <SettingCategories />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.CONTRACT_MANAGEMENT,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ContractManagement />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.CONTRACT_PAYMENT,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ContractPayment />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.CONTRACT_STAFF_LIST,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ContractStaffList />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.PAYMENT_HISTORY,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <PaymentHistory />
                </ProtectedRoutes>
              ),
            },
            // {
            //   path: ROUTES.CONTRACT_MANAGEMENT,
            //   element: <ContractManagement />,
            // },
            {
              path: ROUTES.SETTING_STATUSES,
              children: [
                {
                  index: true,
                  element: (
                    <ProtectedRoutes
                      condition={isAdmin}
                      redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                    >
                      <SettingStatuses />
                    </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.LIST_MESS,
              element: (
                <ProtectedRoutes
                  condition={!isUserResigned}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <ListMess />
                </ProtectedRoutes>
              ),
            },
            {
              path: ROUTES.HOME,
              children: [
                {
                  path: `${ROUTES.LABOR_DOCUMENTS}/${ROUTES.APP_ID}/${ROUTES.CREATE}/${ROUTES.COMPANY_ID}`,
                  element: (
                    <ProtectedRoutes
                      condition={permissionPage[ROUTES.LABOR_DOCUMENTS]?.create}
                      redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    >
                      <LaborDocumentCreate />
                    </ProtectedRoutes>
                  ),
                },
                {
                  path: `${ROUTES.LABOR_DOCUMENTS}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}/${ROUTES.COMPANY_ID}`,
                  element: (
                    // <ProtectedRoutes
                    //   condition={laborDetailPermission}
                    //   redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                    //   specialRoute={true}
                    // >
                    <LaborDocumentEdit />
                    // </ProtectedRoutes>
                  ),
                },
              ],
            },
            {
              path: ROUTES.BUSINESS_FLOW,
              element: (
                <ProtectedRoutes
                  condition={
                    permissionPage[ROUTES.BUSINESS_FLOW]?.all ||
                    permissionPage[ROUTES.BUSINESS_FLOW]?.view ||
                    permissionPage[ROUTES.BUSINESS_FLOW]?.edit
                  }
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessFlowList />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_FLOW}/${ROUTES.APP_ID}/${ROUTES.EDIT}/${ROUTES.ID}`,
              element: (
                <CompanyPolicyProtectedRoute appType={APP_TYPES.BUSINESS_FLOW}>
                  <BusinessFlowEdit />
                </CompanyPolicyProtectedRoute>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_FLOW}/${ROUTES.APP_ID}/${ROUTES.CREATE}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_FLOW]?.create}
                  isLoading={permissionPage[ROUTES.BUSINESS_FLOW]?.isLoading}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessFlowCreate />
                </ProtectedRoutes>
              ),
            },
            {
              path: `${ROUTES.BUSINESS_FLOW}/${ROUTES.APP_ID}/${ROUTES.SHEET}`,
              element: (
                <ProtectedRoutes
                  condition={permissionPage[ROUTES.BUSINESS_FLOW]?.view}
                  redirectTo={`/${ROUTES.NOT_FOUND_PAGE}`}
                >
                  <BusinessFlowSheet />
                </ProtectedRoutes>
              ),
            },
          ],
          errorElement: <ReloadPage />,
        },
        {
          path: ROUTES.CORRELATION_DIAGRAM,
          element: <CommonLayout isHideSideBar={!currentUser} />,
          children: [
            {
              index: true,
              element: <CorrelationDiagramV2 />,
            },
          ],
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={currentUser}
              redirectTo={ROUTES.SIGNIN}
              specialRoute={true}
            >
              <CommonLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ROUTES.PERMISSIONS_MANAGEMENT,
              element: (
                <ProtectedRoutes
                  condition={permission[ROUTES.PERMISSIONS_MANAGEMENT]}
                  redirectTo={`/${ROUTES.CORRELATION_DIAGRAM}`}
                >
                  <AnnualPermissions />
                </ProtectedRoutes>
              ),
            },
          ],
        },
        {
          path: ROUTES.HOME,
          element: (
            <ProtectedRoute
              condition={!currentUser}
              redirectTo={ROUTES.HOME}
              isLoading={false}
              specialRoute={true}
            >
              <Outlet />
            </ProtectedRoute>
          ),
          children: [{ path: ROUTES.SIGNIN, element: <Login /> }],
        },
      ],
    },
  ]);

  if (isLoading && !skipGlobalLoading) return <Loading />;

  return <RouterProvider router={router} />;
};

export default AppRoutes;
